import { array, boolean, discriminatedUnion, enum as enumFactory, literal, number, strictObject, string } from "../../lazy-zod.js";
//#region src/schemas/protocol/agent-interaction.ts
const AGENT_INTERACTION_SCHEMA_VERSION = 1;
const AGENT_INTERACTION_DEFAULT_TIMEOUT_MS = 480 * 60 * 1e3;
const agentInteractionKindSchema = enumFactory([
	"permission",
	"question",
	"plan_review"
]);
const agentInteractionPermissionOptionIntentSchema = enumFactory([
	"allow_once",
	"allow_session",
	"allow_always",
	"deny"
]);
const agentInteractionPermissionOptionSchema = strictObject({
	optionId: string().trim().min(1).max(256),
	label: string().trim().min(1).max(120),
	intent: agentInteractionPermissionOptionIntentSchema
});
const agentInteractionPermissionSubjectSchema = strictObject({
	type: enumFactory([
		"command",
		"file_change",
		"tool",
		"additional_permissions",
		"other"
	]),
	command: string().trim().min(1).max(16e3).nullable().optional(),
	cwd: string().trim().min(1).max(4096).nullable().optional(),
	resource: string().trim().min(1).max(4096).nullable().optional()
});
const agentInteractionQuestionOptionSchema = strictObject({
	optionId: string().trim().min(1).max(256),
	label: string().trim().min(1).max(240),
	description: string().trim().min(1).max(1e3).nullable().optional()
});
const agentInteractionQuestionSchema = strictObject({
	questionId: string().trim().min(1).max(256),
	header: string().trim().min(1).max(120).nullable().optional(),
	prompt: string().trim().min(1).max(4e3),
	options: array(agentInteractionQuestionOptionSchema).max(20),
	multiple: boolean(),
	allowOther: boolean(),
	secret: boolean().optional()
});
const agentInteractionBaseRequestShape = {
	v: literal(1),
	interactionId: string().trim().min(1).max(256),
	requestedAtMs: number().int().nonnegative(),
	expiresAtMs: number().int().nonnegative(),
	title: string().trim().min(1).max(240),
	description: string().trim().min(1).max(4e3).nullable().optional()
};
const agentInteractionRequestSchema = discriminatedUnion("kind", [
	strictObject({
		...agentInteractionBaseRequestShape,
		kind: literal("permission"),
		options: array(agentInteractionPermissionOptionSchema).min(1).max(8),
		subject: agentInteractionPermissionSubjectSchema
	}),
	strictObject({
		...agentInteractionBaseRequestShape,
		kind: literal("question"),
		questions: array(agentInteractionQuestionSchema).min(1).max(3)
	}),
	strictObject({
		...agentInteractionBaseRequestShape,
		kind: literal("plan_review"),
		plan: string().trim().min(1).max(64e3),
		allowFeedback: boolean()
	})
]);
const agentInteractionQuestionAnswerSchema = strictObject({
	questionId: string().trim().min(1).max(256),
	values: array(string().trim().min(1).max(4e3)).min(1).max(20)
});
const agentInteractionResponseSchema = discriminatedUnion("kind", [
	strictObject({
		kind: literal("permission"),
		optionId: string().trim().min(1).max(256)
	}),
	strictObject({
		kind: literal("question"),
		answers: array(agentInteractionQuestionAnswerSchema).min(1).max(3)
	}),
	strictObject({
		kind: literal("plan_review"),
		action: enumFactory(["approve", "request_changes"]),
		feedback: string().trim().min(1).max(8e3).nullable().optional()
	}),
	strictObject({ kind: literal("cancel") })
]);
const resolveQuestionResponseIssue = (input) => {
	const answerByQuestionId = new Map(input.response.answers.map((answer) => [answer.questionId, answer]));
	if (answerByQuestionId.size !== input.request.questions.length || input.response.answers.length !== input.request.questions.length) return {
		message: "question response must answer every question exactly once",
		path: ["response", "answers"]
	};
	for (const question of input.request.questions) {
		const answer = answerByQuestionId.get(question.questionId);
		if (!answer) return {
			message: `missing answer for question '${question.questionId}'`,
			path: ["response", "answers"]
		};
		if (!question.multiple && answer.values.length !== 1) return {
			message: "single-choice questions require exactly one answer",
			path: ["response", "answers"]
		};
		const knownOptionIds = new Set(question.options.map((option) => option.optionId));
		if (!question.allowOther && answer.values.some((value) => !knownOptionIds.has(value))) return {
			message: "question response contains an unavailable option",
			path: ["response", "answers"]
		};
	}
	return null;
};
const resolveAgentInteractionResolutionIssue = (input) => {
	const { request, response } = input;
	if (response.kind === "cancel") return null;
	if (request.kind !== response.kind) return {
		message: "response kind must match the pending interaction",
		path: ["response", "kind"]
	};
	if (request.kind === "permission" && response.kind === "permission") return request.options.some((option) => option.optionId === response.optionId) ? null : {
		message: "permission response must select an available option",
		path: ["response", "optionId"]
	};
	if (request.kind === "plan_review" && response.kind === "plan_review") {
		if (response.action === "request_changes" && !(request.allowFeedback && response.feedback)) return {
			message: "plan change requests require feedback when feedback is allowed",
			path: ["response", "feedback"]
		};
		return response.action === "approve" && response.feedback ? {
			message: "approved plans must not include change feedback",
			path: ["response", "feedback"]
		} : null;
	}
	if (request.kind === "question" && response.kind === "question") return resolveQuestionResponseIssue({
		request,
		response
	});
	return null;
};
const agentInteractionResolutionSchema = strictObject({
	request: agentInteractionRequestSchema,
	response: agentInteractionResponseSchema
}).superRefine((value, context) => {
	const issue = resolveAgentInteractionResolutionIssue(value);
	if (!issue) return;
	context.addIssue({
		code: "custom",
		message: issue.message,
		path: issue.path
	});
});
const agentInteractionPendingSummarySchema = strictObject({
	interactionId: string().trim().min(1).max(256),
	kind: agentInteractionKindSchema,
	requestedAtMs: number().int().nonnegative(),
	expiresAtMs: number().int().nonnegative(),
	canRespond: boolean(),
	request: agentInteractionRequestSchema.nullable()
});
//#endregion
export { AGENT_INTERACTION_DEFAULT_TIMEOUT_MS, AGENT_INTERACTION_SCHEMA_VERSION, agentInteractionKindSchema, agentInteractionPendingSummarySchema, agentInteractionPermissionOptionIntentSchema, agentInteractionPermissionOptionSchema, agentInteractionPermissionSubjectSchema, agentInteractionQuestionAnswerSchema, agentInteractionQuestionOptionSchema, agentInteractionQuestionSchema, agentInteractionRequestSchema, agentInteractionResolutionSchema, agentInteractionResponseSchema };

//# sourceMappingURL=agent-interaction.js.map